import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Search, ShieldCheck, UserRound, Users } from 'lucide-react';
import Pagination from '@app/components/pagination';
import { paginate } from '@app/components/pagination/paginate';
import { getLoading, getStaff } from './redux/selectors';
import { get_staff } from './redux/reducer';
import StaffTable from './table';
import AddStaff from './addstaff';

const ROLE_TABS = [
  { label: 'All', value: '' },
  { label: 'Admins', value: 'admin' },
  { label: 'Staff', value: 'staff' }
];

const matches = (staff, term) =>
  [
    staff?.user?.first_name,
    staff?.user?.last_name,
    staff?.user?.email,
    staff?.phone,
    staff?.city,
    staff?.state,
    staff?.country
  ].some((value) => value?.toLowerCase().includes(term));

function PartnersPage() {
  const dispatch = useDispatch();
  const staffList = useSelector(getStaff);
  const loading = useSelector(getLoading);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    dispatch(get_staff());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return staffList.filter(
      (staff) => (!role || staff.role === role) && (!term || matches(staff, term))
    );
  }, [staffList, search, role]);

  const stats = [
    { label: 'Total members', value: staffList.length, icon: Users },
    {
      label: 'Admins',
      value: staffList.filter((s) => s.role === 'admin').length,
      icon: ShieldCheck
    },
    {
      label: 'Staff',
      value: staffList.filter((s) => s.role === 'staff').length,
      icon: UserRound
    }
  ];

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleRole = (value) => {
    setRole(value);
    setCurrentPage(1);
  };

  const pagedStaff = paginate(filtered, currentPage, pageSize);

  return (
    <div className="px-5 py-6 font-outfit">
      <AddStaff isOpen={showForm} onOpenChange={setShowForm} />

      <div className="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <h1 className="text-[24px] font-semibold text-[#1F2937]">Staff</h1>
          <p className="text-[14px] text-[#6B7280] mt-1">
            Manage the people who have access to the admin dashboard.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="h-[45px] bg-[#549A9A] hover:bg-[#447f7f] transition-colors flex items-center gap-2 justify-center text-[#E2FFEE] font-semibold text-[15px] rounded-[10px] px-6"
        >
          <Plus size={18} />
          Add Staff
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-3">
        {stats.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="flex gap-4 items-center p-4 bg-white rounded-xl border border-[#E9E9E9]"
          >
            <div className="grid place-items-center w-11 h-11 rounded-[10px] bg-[#549A9A]/10 text-[#549A9A]">
              <Icon size={20} />
            </div>
            <div>
              <p className="text-[13px] text-[#6B7280]">{label}</p>
              <p className="text-[22px] font-semibold text-[#1F2937] leading-tight">
                {loading ? '–' : value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#E9E9E9]">
        <div className="flex flex-wrap gap-3 justify-between items-center p-4 border-b border-[#E9E9E9]">
          <div className="flex p-1 rounded-[10px] bg-[#F3F4F6]">
            {ROLE_TABS.map((tab) => (
              <button
                key={tab.label}
                onClick={() => handleRole(tab.value)}
                className={`px-4 py-1.5 text-[14px] font-medium rounded-lg transition-colors ${
                  role === tab.value
                    ? 'bg-white text-[#1F2937] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#1F2937]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-[320px] px-3 rounded-[10px] border border-[#E0E0E0] transition focus-within:border-[#549A9A] focus-within:ring focus-within:ring-[#ACD1D1] focus-within:ring-opacity-30">
            <Search size={18} color="#64748B" />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Search by name, email, phone or location"
              className="py-2.5 w-full text-[14px] bg-transparent focus:outline-none"
            />
          </div>
        </div>

        <StaffTable
          staff={pagedStaff}
          loading={loading}
          isFiltered={Boolean(search || role)}
          onAdd={() => setShowForm(true)}
        />

        {!loading && filtered.length > 0 && (
          <div className="border-t border-[#E9E9E9]">
            <Pagination
              itemsCount={filtered.length}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default PartnersPage;
